import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Input, Select, formatSelectOptions } from '../../../components'
import { storyService } from '../services'
import { appName } from '../appConfig'
import { statusOf } from '../helpers/status'
import '../index.css'

const GROUPS = [
  { cls: 'flagged',   title: 'Att bekräfta' },
  { cls: 'presented', title: 'Genomförda' },
  { cls: 'planned',   title: 'Planerade' },
  { cls: 'idea',      title: 'Idéer' },
]

const groupStories = (stories) => {
  const buckets = { flagged: [], presented: [], planned: [], idea: [] }
  stories.forEach(s => buckets[statusOf(s.story_data || {}).cls].push(s))
  buckets.flagged.sort((a, b) => (a.story_data?.date || '').localeCompare(b.story_data?.date || ''))
  buckets.presented.sort((a, b) => (b.story_data?.date || '').localeCompare(a.story_data?.date || ''))
  buckets.planned.sort((a, b) => (a.story_data?.date || '').localeCompare(b.story_data?.date || ''))
  buckets.idea.sort((a, b) => (a.story_data?.topic || '').localeCompare(b.story_data?.topic || ''))
  return buckets
}

const EMPTY_FILTERS = { search: '', type: '', driver: '', speaker: '', participant: '' }

const applyFilters = (stories, filters) => {
  const search = filters.search.trim().toLowerCase()
  return stories.filter(s => {
    const d = s.story_data || {}
    if (search && !(d.topic || '').toLowerCase().includes(search)) return false
    const displayedType = d.type === 'internal' ? 'internal' : 'external'
    if (filters.type && displayedType !== filters.type) return false
    if (filters.driver && d.driver !== filters.driver) return false
    if (filters.speaker && !(d.speakers || []).some(sp => sp.name === filters.speaker)) return false
    if (filters.participant && !(d.participants || []).some(p => p.name === filters.participant)) return false
    return true
  })
}

const deriveDriverOptions = (stories) => [...new Set(
  stories.map(s => (s.story_data || {}).driver).filter(Boolean)
)].sort((a, b) => a.localeCompare(b))

const deriveSpeakerOptions = (stories) => [...new Set(
  stories.flatMap(s => ((s.story_data || {}).speakers || []).map(sp => sp.name).filter(Boolean))
)].sort((a, b) => a.localeCompare(b))

const deriveParticipantOptions = (stories) => [...new Set(
  stories.flatMap(s => ((s.story_data || {}).participants || []).map(p => p.name).filter(Boolean))
)].sort((a, b) => a.localeCompare(b))

const StoryList = () => {
  const navigate = useNavigate()
  const [stories, setStories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [expanded, setExpanded] = useState(new Set())
  const [collapsedGroups, setCollapsedGroups] = useState(new Set())
  const [filters, setFilters] = useState(EMPTY_FILTERS)
  const [updatingUuid, setUpdatingUuid] = useState(null)
  const [justDoneUuid, setJustDoneUuid] = useState(null)
  const [actionError, setActionError] = useState(null)

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }))

  // Adapter for the shared Input/Select onChange duality: Input passes a native
  // event (or {name,value} from its own clear button), Select always passes
  // {name,value} directly.
  const handleFilterChange = (event) => {
    const { name, value } = event.target !== undefined ? event.target : event
    updateFilter(name, value || '')
  }

  const toggleExpand = (uuid, e) => {
    e.stopPropagation()
    setExpanded(prev => {
      const next = new Set(prev)
      next.has(uuid) ? next.delete(uuid) : next.add(uuid)
      return next
    })
  }

  const toggleGroup = (cls) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      next.has(cls) ? next.delete(cls) : next.add(cls)
      return next
    })
  }

  useEffect(() => {
    const ctrl = new AbortController()
    storyService.getAll(ctrl.signal)
      .then(res => { setStories(res.stories || []); setLoading(false) })
      .catch(err => { if (err.name !== 'AbortError') { setError('Kunde inte ladda stories'); setLoading(false) } })
    return () => ctrl.abort()
  }, [])

  const handleRowClick = (s) => {
    if (s.has_html) navigate(`/${appName}/${s.story_slug}`)
    else            navigate(`/${appName}/${s.story_slug}/edit`)
  }

  // One-directional on purpose — only flagged (overdue, unconfirmed) stories get this
  // quick action. Unmarking a story as done is a deliberate edit, done via the editor.
  const handleMarkDone = async (s, e) => {
    e.stopPropagation()
    setUpdatingUuid(s.story_uuid); setActionError(null)
    try {
      await storyService.update(s.story_uuid, {
        slug: s.story_slug,
        lang: s.story_lang,
        data: { ...(s.story_data || {}), done: true },
      })
      // Flash green before the row actually moves out of "Att bekräfta", so the
      // confirmation is visible instead of the row just vanishing instantly.
      setJustDoneUuid(s.story_uuid)
      setTimeout(() => {
        setStories(prev => prev.map(st => st.story_uuid === s.story_uuid
          ? { ...st, story_data: { ...st.story_data, done: true } }
          : st))
        setJustDoneUuid(null)
      }, 600)
    } catch (err) {
      setActionError('Kunde inte uppdatera status')
    }
    setUpdatingUuid(null)
  }

  if (loading) return <div className="ks-page"><p style={{ color: 'var(--color-dark-grey)' }}>Laddar…</p></div>
  if (error)   return <div className="ks-page"><p style={{ color: 'var(--color-intense-red)' }}>{error}</p></div>

  const hasActiveFilters = Object.values(filters).some(Boolean)
  const driverOptions      = deriveDriverOptions(stories)
  const speakerOptions     = deriveSpeakerOptions(stories)
  const participantOptions = deriveParticipantOptions(stories)

  return (
    <div className="ks-page">
      <div className="ks-toolbar">
        <Button value="+ Ny story" className="button main-button" onClick={() => navigate(`/${appName}/new`)} />

        {stories.length > 0 && (
        <div className="ks-filter-bar">
          <Input
            className="ks-filter-search"
            type="text"
            name="search"
            placeholder="Sök på ämne..."
            value={filters.search}
            onChange={handleFilterChange}
            isNullable
          />
          <Select
            className="ks-filter-select"
            name="type"
            value={filters.type}
            placeholder="Typ"
            options={formatSelectOptions({ options: [{ value: 'internal', label: 'Internt' }, { value: 'external', label: 'Externt' }], optionValue: 'value', optionLabel: 'label' })}
            onChange={handleFilterChange}
            isNullable
          />
          <Select
            className="ks-filter-select"
            name="driver"
            value={filters.driver}
            placeholder="Drivare"
            options={formatSelectOptions({ options: driverOptions.map(name => ({ name })), optionValue: 'name', optionLabel: 'name' })}
            onChange={handleFilterChange}
            isNullable
          />
          <Select
            className="ks-filter-select"
            name="speaker"
            value={filters.speaker}
            placeholder="Talare"
            options={formatSelectOptions({ options: speakerOptions.map(name => ({ name })), optionValue: 'name', optionLabel: 'name' })}
            onChange={handleFilterChange}
            isNullable
          />
          <Select
            className="ks-filter-select"
            name="participant"
            value={filters.participant}
            placeholder="Externa deltagare"
            options={formatSelectOptions({ options: participantOptions.map(name => ({ name })), optionValue: 'name', optionLabel: 'name' })}
            onChange={handleFilterChange}
            isNullable
          />
          {hasActiveFilters && (
            <Button value="Rensa filter" className="button ks-filter-clear" onClick={() => setFilters(EMPTY_FILTERS)} />
          )}
        </div>
        )}
      </div>

      {actionError && <p style={{ color: 'var(--color-intense-red)' }}>{actionError}</p>}

      {stories.length === 0 ? (
        <p style={{ color: 'var(--color-dark-grey)', fontStyle: 'italic' }}>Inga stories ännu. Skapa en för att komma igång.</p>
      ) : (
        (() => {
          const buckets = groupStories(applyFilters(stories, filters))
          const visibleGroups = GROUPS.filter(g => buckets[g.cls].length > 0)
          if (visibleGroups.length === 0) {
            return <p className="ks-no-matches">Inga stories matchar filtren.</p>
          }
          return visibleGroups.map((g, gi) => {
            const isCollapsed = collapsedGroups.has(g.cls)
            return (
            <div className={`ks-table-scroll${gi > 0 ? ' ks-group-table-spaced' : ''}`} key={g.cls}>
            <table className="ks-table ks-group-table">
              <colgroup>
                <col className="ks-col-topic" />
                <col className="ks-col-date" />
                <col className="ks-col-type" />
                <col className="ks-col-speakers" />
                <col className="ks-col-driver" />
                <col className="ks-col-material" />
                <col className="ks-col-actions" />
              </colgroup>
              <thead>
                <tr className="ks-group-header-row ks-group-header-row-clickable" onClick={() => toggleGroup(g.cls)}>
                  <th colSpan={7}>
                    <span className={`ks-group-chevron${isCollapsed ? '' : ' open'}`}>▸</span>
                    <span className={`ks-group-heading ks-group-heading-${g.cls}`}>{g.title}</span>
                    <span className="ks-group-count">{buckets[g.cls].length}</span>
                  </th>
                </tr>
                {!isCollapsed && (
                  <tr>
                    <th>Ämne</th>
                    <th>Datum</th>
                    <th>Typ</th>
                    <th>Talare</th>
                    <th>Drivare</th>
                    <th>Detaljer</th>
                    <th></th>
                  </tr>
                )}
              </thead>
              {!isCollapsed && (
              <tbody>
                {buckets[g.cls].map(s => {
                  const d        = s.story_data || {}
                  const status   = statusOf(d)
                  const speakers     = (d.speakers || []).map(sp => sp.name).filter(Boolean).join(', ')
                  const links        = (d.links || []).filter(l => l && l.url)
                  const participants = (d.participants || []).filter(p => p && p.name)
                  const isOpen       = expanded.has(s.story_uuid)
                  const hasDetails   = links.length > 0 || participants.length > 0
                  const detailParts  = []
                  if (links.length > 0)        detailParts.push(`Material (${links.length})`)
                  if (participants.length > 0) detailParts.push(`Deltagare (${participants.length})`)
                  return (
                    <React.Fragment key={s.story_uuid}>
                      <tr
                        className="ks-row-clickable"
                        title={s.has_html ? 'Öppna one-pager' : 'Inte genererad — öppna editorn'}
                        onClick={() => handleRowClick(s)}
                      >
                        <td style={{ fontFamily: 'Rubik-Medium' }}>
                          <div>{d.topic || s.story_slug}
                            {!s.has_html && <span className="pill pill-grey ks-not-generated">Ej genererad</span>}</div>
                        </td>
                        <td style={{ color: 'var(--color-dark-grey)', fontSize: '0.85em' }}>{d.date || '—'}</td>
                        <td>
                          <span className={`pill ${d.type === 'internal' ? 'pill-full-blue' : 'pill-full-pink'}`}>
                            {d.type === 'internal' ? 'Internt' : 'Externt'}
                          </span>
                        </td>
                        <td style={{ color: 'var(--color-dark-grey)', fontSize: '0.85em' }}>{speakers || '—'}</td>
                        <td style={{ color: 'var(--color-dark-grey)', fontSize: '0.85em' }}>{d.driver || '—'}</td>
                        <td onClick={e => e.stopPropagation()}>
                          {hasDetails ? (
                            <button className="ks-material-toggle" onClick={e => toggleExpand(s.story_uuid, e)}>
                              <span className={`ks-material-chevron${isOpen ? ' open' : ''}`}>▸</span>
                              <span className="ks-material-toggle-labels">
                                {detailParts.map((part, i) => <span key={i}>{part}</span>)}
                              </span>
                            </button>
                          ) : '—'}
                        </td>
                        <td className="ks-actions-cell" onClick={e => e.stopPropagation()}>
                          <div className="ks-actions">
                            <button className="ks-icon-btn" title="Redigera" aria-label="Redigera" onClick={() => navigate(`/${appName}/${s.story_slug}/edit`)}>
                              ✎
                            </button>
                          </div>
                          {status.cls === 'flagged' && (
                            <div className="ks-klarmarkera-float">
                              <span className="ks-klarmarkera-dot" />
                              <span className="ks-klarmarkera-line" />
                              <button
                                className={`ks-mark-done-chip${justDoneUuid === s.story_uuid ? ' success' : ''}`}
                                title="Markera som genomförd"
                                disabled={updatingUuid === s.story_uuid || justDoneUuid === s.story_uuid}
                                onClick={e => handleMarkDone(s, e)}
                              >
                                {justDoneUuid === s.story_uuid ? '✓ Klart!' : updatingUuid === s.story_uuid ? '…' : '✓ Klarmarkera'}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {isOpen && hasDetails && (
                        <tr className="ks-material-row">
                          <td colSpan={7} onClick={e => e.stopPropagation()}>
                            {links.length > 0 && (
                              <>
                                {participants.length > 0 && <div className="ks-detail-section-label">Material</div>}
                                <div className="ks-material-links">
                                  {links.map((l, i) => (
                                    <a key={i} className="ks-material-link" href={l.url} target="_blank" rel="noopener noreferrer">
                                      ↗ {l.label || l.url}
                                    </a>
                                  ))}
                                </div>
                              </>
                            )}
                            {participants.length > 0 && (
                              <>
                                {links.length > 0 && <div className="ks-detail-section-label" style={{ marginTop: 10 }}>Externa deltagare</div>}
                                <div className="ks-material-links">
                                  {participants.map((p, i) => (
                                    <span key={i} className="ks-participant-chip">
                                      <span>{p.name}{p.company ? ` (${p.company})` : ''}</span>
                                      {p.email && (
                                        <a className="ks-participant-contact" href={`mailto:${p.email}`} onClick={e => e.stopPropagation()}>{p.email}</a>
                                      )}
                                      {p.phone && (
                                        <a className="ks-participant-contact" href={`tel:${p.phone}`} onClick={e => e.stopPropagation()}>{p.phone}</a>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  )
                })}
              </tbody>
              )}
            </table>
            </div>
          )})
        })()
      )}
    </div>
  )
}

export default StoryList
